import React, { useState } from "react";
import { useAdmin } from "../hooks/useAdmin";
import { useLanguage } from "../contexts/LanguageContext";
import { X, CheckCircle } from "lucide-react";
import "../styles/AdminLogin.css";

interface AdminLoginProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminLogin({ onClose, onSuccess }: AdminLoginProps) {
  const { t } = useLanguage();
  const { signIn, signInWithMagicLink, error } = useAdmin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError("");

    if (!email) {
      setLocalError(t('admin.enterEmail'));
      return;
    }

    if (useMagicLink) {
      setIsSubmitting(true);
      try {
        await signInWithMagicLink(email);
        setMagicLinkSent(true);
      } catch (err: any) {
        setLocalError(err.message || t('admin.magicLinkError'));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!password) {
        setLocalError(t('admin.enterPassword'));
        return;
      }

      setIsSubmitting(true);
      try {
        await signIn(email, password);
        onSuccess();
      } catch (err: any) {
        setLocalError(err.message || t('admin.signInError'));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="admin-login-overlay" data-testid="admin-login-overlay" onClick={onClose}>
      <div className="admin-login-modal" data-testid="admin-login-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-login-header">
          <h2>{t('admin.login')}</h2>
          <button onClick={onClose} className="close-button" data-testid="admin-login-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form" data-testid="admin-login-form">
          {magicLinkSent ? (
            <div className="success-message">
              <CheckCircle size={20} /> {t('admin.magicLinkSent')}
            </div>
          ) : (
            <>
              {(error || localError) && (
                <div className="error-message" data-testid="admin-login-error">{error || localError}</div>
              )}

              <div className="form-group">
                <label htmlFor="email">{t('admin.email')}</label>
                <input
                  type="email"
                  id="email"
                  data-testid="admin-login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  autoComplete="email"
                  required
                />
              </div>

              {!useMagicLink && (
                <div className="form-group">
                  <label htmlFor="password">{t('admin.password')}</label>
                  <input
                    type="password"
                    id="password"
                    data-testid="admin-login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('admin.passwordPlaceholder')}
                    autoComplete="current-password"
                    required
                  />
                </div>
              )}

              <button type="submit" className="login-button" data-testid="admin-login-submit" disabled={isSubmitting}>
                {isSubmitting
                  ? useMagicLink
                    ? t('admin.sending')
                    : t('admin.signingIn')
                  : useMagicLink
                    ? t('admin.sendMagicLink')
                    : t('admin.signIn')}
              </button>

              <button
                type="button"
                onClick={() => setUseMagicLink(!useMagicLink)}
                className="toggle-auth-button"
                data-testid="admin-login-toggle-method"
                disabled={isSubmitting}
              >
                {useMagicLink
                  ? t('admin.usePassword')
                  : t('admin.useMagicLink')}
              </button>
            </>
          )}
        </form>

        <div className="admin-login-footer">
          <p>{t('admin.footer')}</p>
        </div>
      </div>
    </div>
  );
}
